'use client';

import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useEffect, useRef, useState } from 'react';

const GUEST_CART_IDENTITY = '__guest__';

/**
 * App WebView 不渲染 Header/CartIcon，需要在胶囊页购物车算法分支补齐购物车初始化。
 * 返回值代表当前 App 身份的购物车上下文已经完成初始化尝试；失败时也允许推荐接口降级请求。
 */
export function useCapsuleAppCartRecommendReady(deviceType: EPlatform): boolean {
  const isAppWebView = deviceType === EPlatform.ANDROID || deviceType === EPlatform.IOS;
  const cartDataReady = useStore((state) => state.cartDataReady);
  const currencyReady = useStore((state) => state.currencyReady);
  const setCart = useStore((state) => state.setCart);
  const token = useStore((state) => state.token);
  const cartIdentity = token || GUEST_CART_IDENTITY;
  const [settledIdentity, setSettledIdentity] = useState<string | null>(null);
  const cartDataReadyRef = useRef(cartDataReady);
  const completedIdentityRef = useRef<string | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const latestIdentityRef = useRef(cartIdentity);

  cartDataReadyRef.current = cartDataReady;
  latestIdentityRef.current = cartIdentity;

  const ensureCartReady = useCallback(
    async (expectedIdentity: string) => {
      while (inFlightRef.current) {
        await inFlightRef.current;
      }

      if (
        latestIdentityRef.current !== expectedIdentity ||
        completedIdentityRef.current === expectedIdentity
      ) {
        return;
      }

      const request = setCart()
        .then(() => undefined)
        .catch((error) => {
          logger.error('Failed to initialize capsule App cart recommend context:', error);
        });
      inFlightRef.current = request;

      await request;
      if (inFlightRef.current === request) inFlightRef.current = null;

      if (latestIdentityRef.current === expectedIdentity) {
        completedIdentityRef.current = expectedIdentity;
      }
    },
    [setCart],
  );

  useEffect(() => {
    if (!isAppWebView || !currencyReady) return;

    if (completedIdentityRef.current === cartIdentity) {
      setSettledIdentity(cartIdentity);
      return;
    }

    // 兼容进入胶囊页前已经由其他 App 业务完成购物车初始化的场景。
    if (completedIdentityRef.current === null && cartDataReadyRef.current) {
      completedIdentityRef.current = cartIdentity;
      setSettledIdentity(cartIdentity);
      return;
    }

    setSettledIdentity(null);
    let subscribed = true;
    void ensureCartReady(cartIdentity).then(() => {
      if (
        subscribed &&
        latestIdentityRef.current === cartIdentity &&
        completedIdentityRef.current === cartIdentity
      ) {
        setSettledIdentity(cartIdentity);
      }
    });

    return () => {
      subscribed = false;
    };
  }, [cartDataReady, cartIdentity, currencyReady, ensureCartReady, isAppWebView]);

  return isAppWebView ? settledIdentity === cartIdentity : cartDataReady;
}
